import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { finalize } from 'rxjs/operators';
import { LoanFileShare, ShareRecipientSuggestion } from 'src/app/models/loan-share.models';
import { HelperService } from 'src/app/services/helper.service';
import { LoanShareService } from 'src/app/services/loan-share.service';
import { NotificationService } from 'src/app/services/notification/notification.service';

/**
 * SHARE THIS LOAN FILE WITH THE CUSTOMER — VIEW ONLY.
 *
 * The customer signs in with their ordinary customer portal account. If they do not have one, the
 * invite walks them through registering and the server attaches the share to the new account by
 * email, so nothing here has to care which of the two it is.
 *
 * What the customer gets: the file read-only, its documents to download, and the ability to leave
 * notes. They cannot edit anything. There is no revoke — sharing is a one-way grant — so this dialog
 * lists who the file is shared with and offers to send or re-send, and nothing else.
 *
 * ALWAYS EVERYONE, NEVER ONE. The borrower and co-borrower emails on step 1 are shown for confirmation,
 * and a single button invites all of them together — there is no per-person invite or resend, by
 * product decision, and the server ignores a single address anyway. Each person still gets their own
 * invite, their own portal account and their own view of the file: a joint application means two
 * people who can each sign in, not two people sharing one login. To reach a different address, change
 * it on step 1 — which keeps the file, the invite and the portal account all keyed to the same email.
 *
 * Whether the customer already has a portal account does not matter here. If they do, access is
 * immediate; if they do not, the invite walks them through registering and the server attaches the
 * share to the new account by email.
 *
 * Sharing the same address twice is safe: the server treats it as a resend rather than an error.
 *
 * The server re-checks that the caller is an admin or holds a seat on this file, so hiding the
 * button is presentation, not protection.
 */
@Component({
	selector: 'app-share-with-customer',
	templateUrl: './share-with-customer.component.html',
	styleUrls: ['./share-with-customer.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ButtonsModule,
		DialogsModule
	]
})
export class ShareWithCustomerComponent
{

	@Input() public loanApplicationId: any;

	/**
	 * The file was converted from the customer's own pre-approval, so they can already see it in the
	 * customer portal and there is nothing to share. The server refuses the share either way; this
	 * only stops the button from offering it.
	 */
	@Input() public customerOriginated = false;

	public dialogOpened = false;
	public loading = false;

	/** True while the invite is being sent, so a double click cannot send it twice. */
	public sharing = false;

	public shares: LoanFileShare[] = [];

	/** Everyone step 1 says is on this file. All of them get invited; none of them get picked. */
	public recipients: ShareRecipientSuggestion[] = [];

	constructor (
		private loanShareService: LoanShareService,
		private notification: NotificationService,
		private helperService: HelperService,
	)
	{
	}

	/**
	 * Who may share a file.
	 *
	 * Mirrors the server, which allows an admin or a holder of one of the five staff seats on the
	 * file. The client cannot see the seats, so it checks the role it does have and lets the server
	 * make the real decision.
	 */
	public get canShare (): boolean
	{
		const role = this.helperService.userInfo?.role_id;

		return Boolean(this.loanApplicationId)
			&& !this.customerOriginated
			&& (role === 'ADMIN' || role === 'RB' || role === 'AB');
	}

	/**
	 * Whether step 1 gives us anyone to share with.
	 *
	 * False means the borrower has no email on step 1. There is nothing to type into instead, so the
	 * dialog says so and sends the member to step 1 rather than presenting a form that cannot succeed.
	 */
	public get hasRecipients (): boolean
	{
		return this.recipients.length > 0;
	}

	public open ()
	{
		if (!this.canShare)
		{
			return;
		}

		this.dialogOpened = true;
		this.load();
	}

	public close ()
	{
		this.dialogOpened = false;
	}

	private load ()
	{
		this.loading = true;

		this.loanShareService.listShares(this.loanApplicationId)
			// The interceptor turns a 403 into EMPTY, so the error callback never runs for one.
			// Settling the flag here is what stops the dialog spinning forever on a permission
			// failure.
			.pipe(finalize(() => this.loading = false))
			.subscribe({
				next: (response) =>
				{
					this.shares = response?.data?.shares || [];
					this.recipients = response?.data?.borrowers || [];
				},
				error: (err) => this.notification.fromHttp(err, 'Could not load who this file is shared with.')
			});
	}

	/** Already invited — shown as a marker on that person's row. */
	public isSharedWith (email: string): boolean
	{
		const needle = (email || '').trim().toLowerCase();

		return this.shares.some((share) => (share.recipient_email || '').toLowerCase() === needle);
	}

	/**
	 * One button for everyone, so its label says who it reaches and whether this is a first send.
	 *
	 * "Resend" only once every person on step 1 already has a share. If a co-borrower was added to step
	 * 1 after the first invite, the button says "Send" — the click invites the new person and re-sends
	 * to the ones already invited.
	 */
	public get shareButtonLabel (): string
	{
		if (this.sharing) return 'Sending…';

		const count = this.recipients.length;
		const allInvited = count > 0 && this.recipients.every((r) => this.isSharedWith(r.email));
		const verb = allInvited ? 'Resend invite' : 'Send invite';

		if (count === 1) return verb;

		return `${verb}s to ${count === 2 ? 'both' : 'all'}`;
	}

	/**
	 * Invite every borrower on step 1, together.
	 *
	 * Sends only the file id: the server reads the addresses off step 1 and writes or re-sends one share
	 * per person. Nothing about who receives it is decided here.
	 */
	public share ()
	{
		if (this.sharing || !this.hasRecipients)
		{
			return;
		}

		this.sharing = true;

		this.loanShareService.createShare({ loanapplication_id: this.loanApplicationId })
			.pipe(finalize(() => this.sharing = false))
			.subscribe({
				next: (response) =>
				{
					if (!response?.success)
					{
						this.notification.error(response?.message, 'The file could not be shared.');
						return;
					}

					const results = response.data?.results || [];
					const delivered = results.filter((r) => r.emailsent).map((r) => r.recipient_email);
					const undelivered = results.filter((r) => r.share && !r.emailsent).map((r) => r.recipient_email);
					const skipped = results.filter((r) => r.skipped);

					if (delivered.length)
					{
						this.notification.success(`Invite sent to ${delivered.join(', ')}.`);
					}

					if (undelivered.length)
					{
						// The shares exist either way — the rows are the source of truth, not the
						// emails — so this is a warning to re-send, not a failure.
						this.notification.warning(
							`Shared with ${undelivered.join(', ')}, but the invite email could not be sent. `
							+ 'Use Resend invite to try again.'
						);
					}

					skipped.forEach((r) => this.notification.warning(`${r.recipient_email}: ${r.skipped}`));

					this.load();

					// The notes panel lives in the right side bar, nowhere near this dialog. It
					// subscribes so its "Visible to the customer" option appears without a reload.
					this.helperService.onLoanFileShareChanged.next();
				},
				error: (err) => this.notification.fromHttp(err, 'The file could not be shared.')
			});
	}

	/** What the status column says. The customer-facing meaning, not the raw enum. */
	public statusLabel (share: LoanFileShare): string
	{
		return share.status === 'ACTIVE' ? 'Active' : 'Invited';
	}
}
